/**
 * @fileoverview Profile Page Component
 * @module ProfilePage
 * 
 * Business Context:
 * - Displays user profile information including name, email, and property address
 * - Provides password reset functionality
 * - Accessible from the user dropdown menu in the navigation
 * 
 * Design Context:
 * - Consistent with the Appeal Your Home LandingPage brand aesthetic
 * - Uses Plus Jakarta Sans font and teal color scheme
 * - Clean, simple layout focused on user information
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import {
  User,
  Mail,
  MapPin,
  Key,
  CheckCircle2,
  ArrowLeft,
  Star,
  ChevronDown,
  LogOut,
  Home,
  AlertCircle,
} from 'lucide-react';

const ProfilePage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  // Get primary property from user data
  const primaryProperty = user?.properties?.[0];

  // Load Plus Jakarta Sans font (consistent with LandingPage)
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  // Handle clicking outside the user menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showUserMenu && !target.closest('.relative')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  const handleSignOut = () => {
    logout();
    navigate('/');
  };

  const handlePasswordReset = async () => {
    if (!user?.email) return;
    
    setIsResetting(true);
    setResetError(null);

    try {
      await apiClient.auth.resetPassword(user.email);
      setResetEmailSent(true);
    } catch (error) {
      // For security, show success even on error (backend does the same)
      setResetEmailSent(true);
    } finally {
      setIsResetting(false);
    }
  };

  const Logo = () => (
    <div 
      className="flex items-end gap-2.5 group cursor-pointer select-none" 
      onClick={() => navigate('/dashboard')}
    >
      {/* Logo mark - sized to span both text lines, bottom aligned */}
      <img 
        src="/logo-104.png" 
        alt="" 
        className="w-[34px] h-[34px] object-contain transition-transform duration-200 group-hover:scale-105 mb-[2px]"
      />
      
      {/* Refined wordmark */}
      <div className="flex flex-col items-start">
        <span 
          className="text-[1rem] leading-[1.2] tracking-[-0.015em] text-white"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        >
          <span className="font-semibold">Appeal Your</span>
          {' '}
          <span className="font-extrabold text-teal-400">Home</span>
        </span>
        <span 
          className="text-[0.54rem] font-semibold uppercase tracking-[0.08em] text-gray-400"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", marginTop: '1px' }}
        >
          Learn. Advocate. Save.
        </span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-teal-50/30 to-white" style={{ fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>
      {/* Header - Matches LandingPage Navigation */}
      <nav className="sticky top-0 w-full bg-gray-900/95 backdrop-blur-md shadow-lg z-50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-[4.25rem]">
            <Logo />
            <div className="flex items-center space-x-8">
              <div className="relative">
                <button 
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-3 text-white hover:text-teal-400 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 rounded-lg p-2"
                >
                  <div className="text-right hidden sm:block">
                    <p className="font-semibold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{user?.firstName || 'User'} {user?.lastName || ''}</p>
                    <p className="text-xs text-gray-300 flex items-center gap-1 justify-end" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      <Star className="w-3 h-3 text-teal-400 fill-current" />
                      {user?.tier === 'ai_enhanced' ? 'AI Enhanced' : 'Standard'}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      {user?.firstName?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-300" />
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{user?.firstName} {user?.lastName}</p>
                      <p className="text-xs text-gray-600" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{user?.email}</p>
                    </div>
                    <button
                      onClick={() => { setShowUserMenu(false); navigate('/dashboard'); }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2 transition-colors"
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                      <Home className="w-4 h-4" />
                      <span>Dashboard</span>
                    </button>
                    <button
                      onClick={() => { setShowUserMenu(false); }}
                      className="w-full text-left px-4 py-2 text-sm text-teal-700 bg-teal-50 flex items-center space-x-2"
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                      <User className="w-4 h-4" />
                      <span>Profile</span>
                    </button>
                    <button
                      onClick={() => { setShowUserMenu(false); handleSignOut(); }}
                      className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center space-x-2 transition-colors"
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Back Navigation */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back to Dashboard</span>
        </button>

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Your Profile
          </h1>
          <p className="text-gray-600" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            View your account information and manage your password
          </p>
        </div>

        {/* Profile Information Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <User className="w-5 h-5 text-teal-600" />
            Account Information
          </h2>

          <div className="space-y-6">
            {/* Name */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-teal-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-500 mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Full Name</p>
                <p className="text-lg font-semibold text-gray-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {user?.firstName || 'Not set'} {user?.lastName || ''}
                </p>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-teal-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-500 mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Email Address</p>
                <p className="text-lg font-semibold text-gray-900 break-all" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {user?.email || 'Not set'}
                </p>
              </div>
            </div>

            {/* Subscription Tier */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Star className="w-5 h-5 text-teal-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-500 mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Subscription Tier</p>
                <div className="flex items-center gap-2">
                  <p className="text-lg font-semibold text-gray-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    {user?.tier === 'ai_enhanced' ? 'AI Enhanced' : 'Standard'}
                  </p>
                  {user?.tier === 'ai_enhanced' && (
                    <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-bold rounded-full" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      PRO
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Property Information Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <MapPin className="w-5 h-5 text-teal-600" />
            Property Information
          </h2>

          {primaryProperty ? (
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Home className="w-5 h-5 text-teal-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-500 mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Property Address</p>
                <p className="text-lg font-semibold text-gray-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {primaryProperty.addressLine1}
                  {primaryProperty.addressLine2 && `, ${primaryProperty.addressLine2}`}
                </p>
                <p className="text-gray-600" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {primaryProperty.city}, TX {primaryProperty.zipCode}
                </p>
                {primaryProperty.county && (
                  <p className="text-sm text-gray-500 mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    {primaryProperty.county.name} County
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-gray-500 bg-gray-50 rounded-lg p-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              <AlertCircle className="w-5 h-5" />
              <p>No property associated with your account yet.</p>
            </div>
          )}
        </div>

        {/* Password Reset Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8">
          <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <Key className="w-5 h-5 text-teal-600" />
            Password Management
          </h2>

          {resetEmailSent ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-green-900 mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Password Reset Email Sent
                  </h3>
                  <p className="text-green-700 text-sm mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    We've sent password reset instructions to <strong>{user?.email}</strong>.
                    Please check your inbox and spam folder.
                  </p>
                  <button
                    onClick={() => setResetEmailSent(false)}
                    className="text-green-700 hover:text-green-800 text-sm font-medium underline"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    Didn't receive it? Send again
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Key className="w-5 h-5 text-teal-600" />
              </div>
              <div className="flex-1">
                <p className="text-gray-600 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Need to change your password? We'll send a secure reset link to your email address.
                </p>
                
                {resetError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-red-700" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{resetError}</p>
                  </div>
                )}

                <button
                  onClick={handlePasswordReset}
                  disabled={isResetting}
                  className={`
                    flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold transition-all
                    ${isResetting 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-teal-500 text-white hover:bg-teal-600 shadow-md hover:shadow-lg'
                    }
                  `}
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  {isResetting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Send Password Reset Email
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;



